import { z } from 'zod';

/** Backend DTO (snake_case) — the ONLY place that knows the wire shape. */
export const GroupDtoSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  member_count: z.number().int().min(0),
  currency: z.string().length(3),
  your_balance_minor: z.number().int(),
  created_at: z.string().optional(),
  closed_at: z.string().optional(),
  updated_at: z.string(),
});

export const GroupListDtoSchema = z.object({
  items: z.array(GroupDtoSchema),
});

export type GroupDto = z.infer<typeof GroupDtoSchema>;

/**
 * Wire shape of a group from the real backend `GET /api/v1/group` (snake_case). Only the fields
 * the rail consumes are declared; unknown keys are ignored. `state=all` returns every group with
 * its `is_archived` / `is_closed` flags so the tabs can filter client-side.
 */
/** Group member (subset) — count, settlement-name resolution, and the member avatars. */
const GroupMemberSchema = z.object({
  user_id: z.string().nullish(),
  name: z.string().nullish(),
  full_name: z.string().nullish(),
  profile_picture_url: z.string().nullish(),
  phone_number: z.string().nullish(),
  email: z.string().nullish(),
  role: z.string().nullish(),
  is_guest: z.boolean().nullish(),
  is_pro: z.boolean().nullish(),
  // Some feeds mark the group creator on their MEMBERSHIP row instead of a group-level id —
  // a fallback source for the members dialog's "Creator" tag.
  is_creator: z.boolean().nullish(),
});

/** A settlement row from `settle_data`: `{ from:{id,type}, to:{id,type}, amount }` (minor units). */
const SettleEntrySchema = z.object({
  from: z.object({ id: z.string(), type: z.string() }),
  to: z.object({ id: z.string(), type: z.string() }),
  amount: z.number(),
});

export const GroupIndexItemSchema = z.object({
  _id: z.string(),
  name: z.string(),
  type: z.string().default('others'),
  image_url: z.string().nullish(),
  is_archived: z.boolean().default(false),
  is_closed: z.boolean().default(false),
  current_statement_balance: z.number().default(0),
  total_shared_amount: z.number().default(0),
  total_private_amount: z.number().default(0),
  bookkeeper_ids: z.array(z.string()).nullish(),
  currency: z
    .object({
      currency_code: z.string().default('USD'),
      currency_symbol: z.string().default(''),
    })
    .nullish(),
  // The current user's preferred entry currency for this group (drives the add-expense default).
  user_preferred_currency_id: z.string().nullish(),
  // Whether the wallet FEATURE is turned on for this group, separate from the `wallet` object below
  // (which the backend may still send stats for even when the feature toggle is off) — `.default(true)`
  // so a response that omits this field entirely (unconfirmed whether every endpoint sends it) keeps
  // today's behaviour; only an EXPLICIT `false` suppresses the wallet as a payer option.
  virtual_wallet: z.boolean().default(true),
  // The wallet's own account id (`wallet_id`) is the `paid_by` id for a wallet payer — distinct from
  // any member's user id. Accept `_id`/`id` too defensively; the mapper falls back to a settlement id.
  wallet: z
    .object({
      wallet_id: z.string().nullish(),
      _id: z.string().nullish(),
      id: z.string().nullish(),
      current_balance: z.number().default(0),
      // The wallet's book-keepers (members who manage its books). When the signed-in user is NOT in
      // this list, the add-expense Paid-by field shows a "not book-keeper" hint glow.
      bookkeeper_user_ids: z.array(z.string()).nullish(),
      is_wallet_close: z.boolean().nullish(),
    })
    .nullish(),
  users: z.array(GroupMemberSchema).default([]),
  settle_data: z.array(SettleEntrySchema).nullish(),
  last_transaction_at: z.string().nullish(),
  start_date: z.string().nullish(),
  end_date: z.string().nullish(),
  created_at: z.string().nullish(),
  closed_at: z.string().nullish(),
  // The group creator's user id — `owner_id` is the field mobile actually reads
  // (`editGroupModel.ownerId` drives its "(Creator)" tag); the others are kept as defensive
  // aliases (`created_by_user_id` appears on other records; `user_id` is the backend's standard
  // document-creator key, e.g. on expenses). Drives the members dialog's "Creator" tag.
  owner_id: z.string().nullish(),
  created_by: z.string().nullish(),
  created_by_user_id: z.string().nullish(),
  user_id: z.string().nullish(),
});

/** Envelope of `GET /api/v1/group`: `{ status, message, data: { groups, deleted } }`. */
export const GroupIndexResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().optional(),
  data: z
    .object({
      groups: z.array(GroupIndexItemSchema).default([]),
      deleted: z.array(z.string()).default([]),
    })
    .default({ groups: [], deleted: [] }),
});

export type GroupIndexItem = z.infer<typeof GroupIndexItemSchema>;
